import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import {
  BarcodeDetailIcon,
  PalletDetailIcon,
} from "../Icons/ProductDetailIcons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addToCart } from "../../store";
import { productToCartItem } from "../utils/productToCartItem";
import { fetchAllBrands, fetchProductsForSearch } from "../utils/shopApi";
import {
  fetchShopCategories,
  findShopCategory,
  findShopSubcategory,
  getFiltersForShopContext,
} from "../Navbar/navConfig";

const PRODUCT_CARD_BLUE = "#0b3f7b";
const PRODUCT_CARD_RED = "#e31837";
const PRODUCT_LIST_PAGE_SIZE = 24;

const normalizeBrandKey = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const buildBrandLookup = (brands = []) => {
  const lookup = new Map();
  brands.forEach((brand) => {
    const name = String(brand?.name ?? "").trim();
    const key = normalizeBrandKey(name);
    if (name && key.length >= 2 && !lookup.has(key)) {
      lookup.set(key, name);
    }
  });
  return lookup;
};

const resolveProductBrandName = (product, brandLookup) => {
  if (product?.brand?.name) return product.brand.name;
  if (product?.manufacturer) return product.manufacturer;

  const words = String(product?.name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "Other";

  const maxWords = Math.min(words.length, 6);
  for (let count = maxWords; count >= 1; count -= 1) {
    const key = normalizeBrandKey(words.slice(0, count).join(" "));
    if (brandLookup.has(key)) return brandLookup.get(key);
  }

  return words[0] || "Other";
};

const getProductDetailRows = (product) => [
  {
    icon: LocalOfferOutlinedIcon,
    label: "Product Code:",
    value: product?.sku || product?.code || "-",
  },
  {
    icon: Inventory2OutlinedIcon,
    label: "Case Size:",
    value: product?.case_size || "-",
  },
  {
    icon: LayersOutlinedIcon,
    label: "Layer:",
    value: product?.layer || "-",
  },
  {
    icon: PalletDetailIcon,
    label: "Pallet:",
    value: product?.pallet || "-",
  },
  {
    icon: BarcodeDetailIcon,
    label: "Barcode:",
    value: product?.unit_barcode || product?.sku || "-",
  },
];

const pageShell = (children) => (
  <Box
    sx={{
      pt: "clamp(96px, 12vh, 136px)",
      pb: { xs: 4, md: 6 },
      px: { xs: 1.5, md: 2.5 },
      maxWidth: 1400,
      mx: "auto",
    }}
  >
    {children}
  </Box>
);

export function SearchPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toSlug = (value = "") =>
    value
      .toLowerCase()
      .trim()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const location = useLocation();
  const [params] = useSearchParams();
  const category = params.get("category")?.toLowerCase() ?? "";
  const subcategory = params.get("subcategory")?.toLowerCase() ?? "";
  const q = params.get("q")?.toLowerCase().trim() ?? "";
  const [items, setItems] = useState([]);
  const [shopCategories, setShopCategories] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [brandSearch, setBrandSearch] = useState("");
  const [listMeta, setListMeta] = useState({ total: 0, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("name-asc");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState(() =>
    subcategory ? [subcategory] : category ? [category] : [],
  );
  const [addedProductIds, setAddedProductIds] = useState([]);
  const [refineDrawerOpen, setRefineDrawerOpen] = useState(false);
  const [cartNotice, setCartNotice] = useState({ open: false, message: "" });

  useEffect(() => {
    setSelectedFilters([]);
    setSelectedBrands([]);
    setSelectedCategorySlugs(
      subcategory ? [subcategory] : category ? [category] : [],
    );
  }, [category, subcategory, q]);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const [currentPage, setCurrentPage] = useState(1);

  const categoryParam = (
    selectedCategorySlugs[0] ||
    subcategory ||
    category ||
    ""
  ).trim();

  const selectedBrandIds = useMemo(() => {
    const ids = [];
    const seen = new Set();
    selectedBrands.forEach((name) => {
      const match = allBrands.find(
        (brand) =>
          String(brand?.name ?? "").toLowerCase() ===
          String(name ?? "").toLowerCase(),
      );
      if (match?.id == null) return;
      const id = String(match.id);
      if (seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
    return ids;
  }, [selectedBrands, allBrands]);

  const selectedBrandId = selectedBrandIds[0] || "";

  const brandLookup = useMemo(() => buildBrandLookup(allBrands), [allBrands]);

  useEffect(() => {
    let isMounted = true;

    const loadTaxonomy = async () => {
      try {
        const [categories, brands] = await Promise.all([
          fetchShopCategories().catch(() => []),
          fetchAllBrands().catch(() => []),
        ]);
        if (isMounted) {
          setShopCategories(categories);
          setAllBrands(brands);
        }
      } catch {
        if (isMounted) {
          setShopCategories([]);
          setAllBrands([]);
        }
      }
    };

    loadTaxonomy();
    return () => {
      isMounted = false;
    };
  }, []);

  const getBrandName = useCallback(
    (item) => resolveProductBrandName(item, brandLookup),
    [brandLookup],
  );

  const getProductFilterSlugs = useCallback(
    (product) =>
      (product?.filters ?? [])
        .map(
          (filter) => filter?.slug?.toLowerCase() || toSlug(filter?.name ?? ""),
        )
        .filter(Boolean),
    [],
  );

  const productMatchesListingContext = useCallback(
    (
      product,
      { includeSelectedFilters = true, includeSelectedBrands = true } = {},
    ) => {
      const productCategory = product?.category?.slug?.toLowerCase() ?? "";
      const productFilterSlugs = getProductFilterSlugs(product);
      const brandName = getBrandName(product);

      const matchesSelectedFilters =
        includeSelectedFilters && selectedFilters.length
          ? productFilterSlugs.length
            ? selectedFilters.every((selected) =>
                productFilterSlugs.includes(selected.toLowerCase()),
              )
            : true
          : true;
      const matchesSelectedBrands =
        includeSelectedBrands &&
        selectedBrands.length &&
        selectedBrandIds.length !== selectedBrands.length
          ? selectedBrands.some((selected) => {
              if (selected === brandName) return true;
              const selectedKey = normalizeBrandKey(selected);
              const productKey = normalizeBrandKey(product?.name);
              return (
                selectedKey.length >= 2 && productKey.includes(selectedKey)
              );
            })
          : true;
      const matchesSelectedCategories =
        selectedCategorySlugs.length > 1
          ? selectedCategorySlugs.some((selected) => {
              const selectedSlug = selected.toLowerCase();
              return (
                productCategory === selectedSlug ||
                productCategory.startsWith(`${selectedSlug}-`)
              );
            })
          : true;

      return (
        matchesSelectedFilters &&
        matchesSelectedBrands &&
        matchesSelectedCategories
      );
    },
    [
      selectedFilters,
      selectedBrands,
      selectedCategorySlugs,
      selectedBrandIds,
      getProductFilterSlugs,
      getBrandName,
    ],
  );

  const sortFilters = (filters) =>
    [...filters].sort((a, b) => {
      const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      return orderDiff !== 0
        ? orderDiff
        : (a.label ?? "").localeCompare(b.label ?? "");
    });

  const availableFilters = useMemo(() => {
    if (shopCategories.length && selectedCategorySlugs.length) {
      const selectedSet = new Set(
        selectedCategorySlugs.map((slug) => slug.toLowerCase()),
      );
      const seen = new Set();
      const filters = [];

      shopCategories.forEach((parent) => {
        const parentSelected = selectedSet.has(parent.slug?.toLowerCase());
        (parent.subcategories ?? []).forEach((child) => {
          const childSelected =
            parentSelected || selectedSet.has(child.slug?.toLowerCase());
          if (!childSelected) return;

          (child.filters ?? []).forEach((filter) => {
            const filterSlug = filter?.slug?.toLowerCase();
            if (!filterSlug || seen.has(filterSlug)) return;
            seen.add(filterSlug);
            filters.push(filter);
          });
        });
      });

      return sortFilters(filters);
    }

    const fromCategories = getFiltersForShopContext(shopCategories, {
      category,
      subcategory,
    });
    if (fromCategories.length) {
      return sortFilters(fromCategories);
    }

    if (category || subcategory) return [];

    const seen = new Map();
    items.forEach((product) => {
      (product?.filters ?? []).forEach((filter) => {
        const slug = filter?.slug || toSlug(filter?.name ?? "");
        if (!slug || seen.has(slug)) return;
        seen.set(slug, {
          label: filter?.name ?? slug,
          slug,
          sort_order: filter?.sort_order ?? 0,
        });
      });
    });

    return sortFilters(Array.from(seen.values()));
  }, [shopCategories, selectedCategorySlugs, category, subcategory, items]);

  useEffect(() => {
    setSelectedFilters((prev) => {
      if (!prev.length) return prev;
      const availableSlugs = new Set(
        availableFilters.map((filter) => filter.slug.toLowerCase()),
      );
      const next = prev.filter((slug) =>
        availableSlugs.has(slug.toLowerCase()),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [availableFilters]);

  const categoryFilterTree = useMemo(() => {
    const mapChildren = (entries = []) =>
      entries.map((entry) => ({
        label: entry.label,
        slug: entry.slug,
      }));

    if (shopCategories.length) {
      return shopCategories.map((entry) => ({
        label: entry.label,
        slug: entry.slug,
        children: mapChildren(entry.subcategories),
      }));
    }

    const seen = new Map();
    items.forEach((product) => {
      const slug = product?.category?.slug?.toLowerCase();
      const label = product?.category?.name ?? slug;
      if (!slug || seen.has(slug)) return;
      seen.set(slug, { label, slug, children: [] });
    });

    return Array.from(seen.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [shopCategories, items]);

  const hasCategoryFilterOptions = categoryFilterTree.length > 0;

  const availableBrands = useMemo(() => {
    const names = new Set();
    items.forEach((product) => {
      if (
        !productMatchesListingContext(product, {
          includeSelectedBrands: false,
        })
      ) {
        return;
      }
      names.add(getBrandName(product));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [items, productMatchesListingContext, getBrandName]);

  const visibleBrandNames = useMemo(() => {
    const query = brandSearch.trim().toLowerCase();
    const selected = selectedBrands.filter(Boolean);
    if (query) {
      const fromAll = allBrands
        .map((brand) => brand?.name)
        .filter((name) => name && name.toLowerCase().includes(query));
      return [
        ...selected.filter((name) => !fromAll.includes(name)),
        ...fromAll,
      ].slice(0, 60);
    }
    return [
      ...selected.filter((name) => !availableBrands.includes(name)),
      ...availableBrands,
    ];
  }, [availableBrands, brandSearch, selectedBrands, allBrands]);

  useEffect(() => {
    setSelectedBrands((prev) => {
      if (!prev.length || !allBrands.length) return prev;
      const validNames = new Set(
        allBrands.map((brand) => brand?.name).filter(Boolean),
      );
      const next = prev.filter((brandName) => validNames.has(brandName));
      return next.length === prev.length ? prev : next;
    });
  }, [allBrands]);

  const categoryLabel = useMemo(() => {
    if (!category) return "";
    return findShopCategory(shopCategories, category)?.label ?? category;
  }, [shopCategories, category]);

  const subcategoryLabel = useMemo(() => {
    if (!subcategory) return "";
    return (
      findShopSubcategory(shopCategories, subcategory)?.label ?? subcategory
    );
  }, [shopCategories, subcategory]);

  const filteredAndSortedProducts = useMemo(() => {
    const list = items.filter((product) =>
      productMatchesListingContext(product),
    );

    return [...list].sort((a, b) => {
      const priceA = Number.parseFloat(a?.price ?? 0);
      const priceB = Number.parseFloat(b?.price ?? 0);
      switch (sortBy) {
        case "price-asc":
          return priceA - priceB;
        case "price-desc":
          return priceB - priceA;
        case "name-desc":
          return (b?.name ?? "").localeCompare(a?.name ?? "");
        case "name-asc":
        default:
          return (a?.name ?? "").localeCompare(b?.name ?? "");
      }
    });
  }, [items, sortBy, productMatchesListingContext]);

  const totalCount = Number(listMeta.total) || 0;
  const totalPages = Math.max(1, Number(listMeta.last_page) || 1);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    category,
    subcategory,
    q,
    selectedBrands,
    selectedFilters,
    selectedCategorySlugs,
  ]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      try {
        setLoading(true);
        const result = await fetchProductsForSearch({
          page: currentPage,
          perPage: PRODUCT_LIST_PAGE_SIZE,
          q,
          category: categoryParam,
          brandId: selectedBrandId,
          brandIds: selectedBrandIds,
          filters: selectedFilters,
        });

        if (isMounted) {
          setItems(
            result.data.filter((product) => product?.is_active !== false),
          );
          setListMeta({
            total: Number(result.meta?.total) || result.data.length,
            last_page: Number(result.meta?.last_page) || 1,
          });
          setError("");
        }
      } catch {
        if (isMounted) {
          setItems([]);
          setListMeta({ total: 0, last_page: 1 });
          setError("Unable to load products right now.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProducts();
    return () => {
      isMounted = false;
    };
  }, [
    currentPage,
    q,
    categoryParam,
    selectedBrandId,
    selectedBrandIds,
    selectedFilters,
  ]);

  const paginatedProducts = filteredAndSortedProducts;

  const toggleListValue = (list, setter, value) => {
    setter(
      list.includes(value)
        ? list.filter((entry) => entry !== value)
        : [...list, value],
    );
    setCurrentPage(1);
  };

  const getProductImage = (product) => {
    if (product?.image_url) return product.image_url;
    if (
      typeof product?.image === "string" &&
      product.image.startsWith("http")
    ) {
      return product.image;
    }
    if (typeof product?.image === "string" && product.image) {
      return `https://api.uktomars.co.uk/storage/${product.image}`;
    }
    return "";
  };

  const handleAddToCart = (product) => {
    if (!product?.id) return;
    dispatch(addToCart(productToCartItem(product, getProductImage)));
    setAddedProductIds((prev) =>
      prev.includes(product.id) ? prev : [...prev, product.id],
    );
    const label = (product?.name ?? "Item").trim() || "Item";
    setCartNotice({ open: true, message: `${label} added to cart` });
  };

  const openProductDetails = (product) => {
    if (!product?.id) return;
    navigate(`/products/${product.id}`, { state: { product } });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  const refinePanelBody = (
    <>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: 17,
          mb: 1.25,
          color: "#0f172a",
          letterSpacing: 0.2,
        }}
      >
        Filter
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 2.1 }}>
        <InputLabel id="products-sort-label">Sort</InputLabel>
        <Select
          labelId="products-sort-label"
          value={sortBy}
          label="Sort"
          onChange={(event) => setSortBy(event.target.value)}
          sx={{
            borderRadius: 1.4,
            bgcolor: "#f8fafc",
            "& .MuiSelect-select": {
              fontSize: 14,
              fontWeight: 600,
              color: "#1f2937",
            },
          }}
        >
          <MenuItem value="name-asc">Name A-Z</MenuItem>
          <MenuItem value="name-desc">Name Z-A</MenuItem>
          <MenuItem value="price-asc">Price Low to High</MenuItem>
          <MenuItem value="price-desc">Price High to Low</MenuItem>
        </Select>
      </FormControl>

      {hasCategoryFilterOptions ? (
        <Box sx={{ borderTop: "1px solid #edf2f7", pt: 1.7, mt: 0.6 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 13,
              mb: 1,
              color: "#0b3f7b",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            Categories
          </Typography>
          <Stack
            spacing={0.35}
            sx={{ maxHeight: 280, overflowY: "auto", pr: 0.3 }}
          >
            {categoryFilterTree.map((parent) => (
              <Box key={parent.slug}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedCategorySlugs.includes(parent.slug)}
                      onChange={() =>
                        toggleListValue(
                          selectedCategorySlugs,
                          setSelectedCategorySlugs,
                          parent.slug,
                        )
                      }
                    />
                  }
                  label={parent.label}
                  sx={{
                    m: 0,
                    py: 0.3,
                    px: 0.55,
                    borderRadius: 1,
                    alignItems: "flex-start",
                    width: "100%",
                    bgcolor: selectedCategorySlugs.includes(parent.slug)
                      ? "#eef6ff"
                      : "transparent",
                    "&:hover": { bgcolor: "#f8fafc" },
                    "& .MuiCheckbox-root": {
                      py: 0.1,
                      alignSelf: "flex-start",
                    },
                    "& .MuiFormControlLabel-label": {
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                      whiteSpace: "normal",
                      lineHeight: 1.35,
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      display: "block",
                      pt: 0.15,
                    },
                  }}
                />
                {(parent.children ?? []).map((child) => (
                  <FormControlLabel
                    key={child.slug}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedCategorySlugs.includes(child.slug)}
                        onChange={() =>
                          toggleListValue(
                            selectedCategorySlugs,
                            setSelectedCategorySlugs,
                            child.slug,
                          )
                        }
                      />
                    }
                    label={child.label}
                    sx={{
                      m: 0,
                      ml: 2.5,
                      py: 0.25,
                      px: 0.55,
                      borderRadius: 1,
                      alignItems: "flex-start",
                      width: "calc(100% - 20px)",
                      bgcolor: selectedCategorySlugs.includes(child.slug)
                        ? "#eef6ff"
                        : "transparent",
                      "&:hover": { bgcolor: "#f8fafc" },
                      "& .MuiCheckbox-root": {
                        py: 0.1,
                        alignSelf: "flex-start",
                      },
                      "& .MuiFormControlLabel-label": {
                        fontSize: 12.5,
                        fontWeight: 500,
                        color: "#475569",
                        whiteSpace: "normal",
                        lineHeight: 1.35,
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        display: "block",
                        pt: 0.15,
                      },
                    }}
                  />
                ))}
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}

      {availableFilters.length > 0 ? (
        <Box sx={{ borderTop: "1px solid #edf2f7", pt: 1.7, mt: 0.6 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 13,
              mb: 1,
              color: "#0b3f7b",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            Filters
          </Typography>
          <Stack
            spacing={0.35}
            sx={{ maxHeight: 240, overflowY: "auto", pr: 0.3 }}
          >
            {availableFilters.map((filter) => (
              <FormControlLabel
                key={filter.slug}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedFilters.includes(filter.slug)}
                    onChange={() =>
                      toggleListValue(
                        selectedFilters,
                        setSelectedFilters,
                        filter.slug,
                      )
                    }
                  />
                }
                label={filter.label}
                sx={{
                  m: 0,
                  py: 0.3,
                  px: 0.55,
                  borderRadius: 1,
                  bgcolor: selectedFilters.includes(filter.slug)
                    ? "#eef6ff"
                    : "transparent",
                  "&:hover": { bgcolor: "#f8fafc" },
                  "& .MuiFormControlLabel-label": {
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#334155",
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      ) : null}

      {availableBrands.length > 0 ? (
        <Box sx={{ borderTop: "1px solid #edf2f7", pt: 1.7, mt: 1.15 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 13,
              mb: 1,
              color: "#0b3f7b",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            Brands
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Search brands"
            value={brandSearch}
            onChange={(event) => setBrandSearch(event.target.value)}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                fontSize: 13,
                bgcolor: "#ffffff",
              },
            }}
          />
          <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.75 }}>
            Brands in this category. Search to narrow the list.
          </Typography>
          <Stack
            spacing={0.35}
            sx={{ maxHeight: 220, overflowY: "auto", pr: 0.3 }}
          >
            {visibleBrandNames.map((brandName) => (
              <FormControlLabel
                key={brandName}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedBrands.includes(brandName)}
                    onChange={() =>
                      toggleListValue(
                        selectedBrands,
                        setSelectedBrands,
                        brandName,
                      )
                    }
                  />
                }
                label={brandName}
                sx={{
                  m: 0,
                  py: 0.3,
                  px: 0.55,
                  borderRadius: 1,
                  bgcolor: selectedBrands.includes(brandName)
                    ? "#eef6ff"
                    : "transparent",
                  "&:hover": { bgcolor: "#f8fafc" },
                  "& .MuiFormControlLabel-label": {
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#334155",
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </>
  );

  return pageShell(
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 1.5, md: 2.5 },
          alignItems: "flex-start",
          width: "100%",
          mt: { xs: 0, md: 1 },
        }}
      >
        {isMdUp ? (
          <Card
            elevation={0}
            sx={{
              width: 260,
              flexShrink: 0,
              p: 2.1,
              border: "1px solid #dbe3ee",
              borderRadius: 2.5,
              bgcolor: "#ffffff",
              boxShadow: "0 14px 34px rgba(16, 24, 40, 0.08)",
              position: "sticky",
              top: 108,
            }}
          >
            {refinePanelBody}
          </Card>
        ) : null}

        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {!isMdUp ? (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setRefineDrawerOpen(true)}
              sx={{
                mb: 1.5,
                py: 1.1,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                borderColor: "#cbd5e1",
                color: "#0f172a",
                borderRadius: 2,
                "&:hover": {
                  borderColor: "#0b3f7b",
                  bgcolor: "rgba(11, 63, 123, 0.04)",
                },
              }}
            >
              Filter
            </Button>
          ) : null}
          <Box
            sx={{
              mb: 2,
              p: { xs: 1.25, md: 1.5 },
              border: "1px solid #e3e7ee",
              borderRadius: 2,
              background: "linear-gradient(180deg, #f8fbff 0%, #f5f8fc 100%)",
            }}
          >
            <Typography
              sx={{ fontSize: 14, color: "#374151", fontWeight: 500 }}
            >
              {loading
                ? "Loading products..."
                : `${totalCount} product(s) found`}
            </Typography>
            {(category || q || subcategory) && (
              <Typography sx={{ fontSize: 13, color: "#7a8494", mt: 0.4 }}>
                {category ? `Category: ${categoryLabel} ` : ""}
                {subcategory ? `| Subcategory: ${subcategoryLabel} ` : ""}
                {q ? `| Search: "${q}"` : ""}
              </Typography>
            )}
          </Box>

          {error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
                gap: { xs: 1, sm: 1.5, md: 2 },
              }}
            >
              {paginatedProducts.map((product) => (
                <Card
                  key={product.id}
                  elevation={0}
                  sx={{
                    border: "1px solid #dbe3ee",
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 4px 16px rgba(16, 24, 40, 0.06)",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 24px rgba(16, 24, 40, 0.1)",
                      borderColor: "#c5d3e3",
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: { xs: 120, sm: 165, md: 175 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#ffffff",
                      px: { xs: 1, sm: 2 },
                      pt: { xs: 1.25, sm: 2 },
                      pb: { xs: 1, sm: 1.5 },
                      cursor: "pointer",
                    }}
                    onClick={() => openProductDetails(product)}
                  >
                    {getProductImage(product) ? (
                      <img
                        src={getProductImage(product)}
                        alt={product.name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: "#98a1af", fontSize: 13 }}>
                        No image
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      height: 3,
                      bgcolor: PRODUCT_CARD_BLUE,
                      flexShrink: 0,
                    }}
                  />

                  <Box
                    sx={{
                      px: { xs: 1, sm: 1.5, md: 2 },
                      pt: { xs: 1.25, sm: 1.5 },
                      pb: { xs: 1, sm: 1.25 },
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: 11.5, sm: 13, md: 14 },
                        fontWeight: 700,
                        color: PRODUCT_CARD_BLUE,
                        mb: { xs: 0.75, sm: 1 },
                        lineHeight: 1.35,
                        cursor: "pointer",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                      onClick={() => openProductDetails(product)}
                    >
                      {product.name}
                    </Typography>

                    <Box sx={{ flex: 1 }}>
                      {getProductDetailRows(product).map((row, index, rows) => {
                        const RowIcon = row.icon;
                        return (
                          <Box
                            key={row.label}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: { xs: 0.5, sm: 1 },
                              py: { xs: 0.6, sm: 0.85 },
                              borderBottom:
                                index < rows.length - 1
                                  ? "1px solid #e8edf3"
                                  : "none",
                            }}
                          >
                            <RowIcon
                              sx={{
                                fontSize: { xs: 15, sm: 18 },
                                color: PRODUCT_CARD_BLUE,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: { xs: 10, sm: 11.5, md: 12.5 },
                                fontWeight: 700,
                                color: PRODUCT_CARD_BLUE,
                                flexShrink: 0,
                              }}
                            >
                              {row.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: { xs: 10, sm: 11.5, md: 12.5 },
                                fontWeight: 500,
                                color: "#1e293b",
                                ml: "auto",
                                textAlign: "right",
                                wordBreak: "break-word",
                              }}
                            >
                              {row.value}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleAddToCart(product)}
                      startIcon={
                        addedProductIds.includes(product.id) ? (
                          <CheckCircleOutlineIcon
                            sx={{ fontSize: { xs: 16, sm: 20 } }}
                          />
                        ) : (
                          <AddShoppingCartOutlinedIcon
                            sx={{ fontSize: { xs: 16, sm: 20 } }}
                          />
                        )
                      }
                      sx={{
                        mt: { xs: 1, sm: 1.5 },
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: { xs: 11, sm: 13, md: 14 },
                        borderRadius: 1.5,
                        py: { xs: 0.85, sm: 1.1 },
                        px: { xs: 0.5, sm: 1 },
                        bgcolor: PRODUCT_CARD_RED,
                        boxShadow: "0 4px 12px rgba(227, 24, 55, 0.28)",
                        "&:hover": {
                          bgcolor: "#c8102e",
                          boxShadow: "0 6px 16px rgba(227, 24, 55, 0.34)",
                        },
                      }}
                    >
                      {addedProductIds.includes(product.id)
                        ? "Added"
                        : "Add to Quote"}
                    </Button>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
          {!loading && !error && totalCount > 0 && totalPages > 1 ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                shape="rounded"
                sx={{
                  "& .MuiPagination-ul": {
                    gap: 0.4,
                  },
                  "& .MuiPaginationItem-root": {
                    minWidth: 34,
                    height: 34,
                    borderRadius: 1.3,
                    border: "1px solid #d7dfeb",
                    color: "#1e293b",
                    fontWeight: 700,
                    fontSize: 13,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#0b3f7b",
                      backgroundColor: "rgba(11, 63, 123, 0.08)",
                    },
                  },
                  "& .MuiPaginationItem-root.Mui-selected": {
                    color: "#ffffff",
                    borderColor: "#0b3f7b",
                    background:
                      "linear-gradient(90deg, #0f2a7a 0%, #111b4f 100%)",
                    boxShadow: "0 6px 14px rgba(15, 42, 122, 0.24)",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, #0c2365 0%, #0d163f 100%)",
                    },
                  },
                }}
              />
            </Box>
          ) : null}
          {!loading && !error && filteredAndSortedProducts.length === 0 ? (
            <Typography sx={{ mt: 2, color: "#6b7280" }}>
              No products match these filters.
            </Typography>
          ) : null}

          <Typography variant="body2" sx={{ mt: 2 }}>
            <Link to="/">Back to home</Link>
          </Typography>
        </Box>
      </Box>

      {!isMdUp ? (
        <Drawer
          anchor="left"
          open={refineDrawerOpen}
          onClose={() => setRefineDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: "min(100vw - 16px, 320px)",
              maxWidth: "100%",
              p: 0,
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #dbe3ee",
              boxShadow: "8px 0 32px rgba(16, 24, 40, 0.12)",
            },
          }}
        >
          <Box sx={{ flex: 1, overflow: "auto", p: 2.1, bgcolor: "#ffffff" }}>
            {refinePanelBody}
          </Box>
          <Box
            sx={{
              p: 2,
              pt: 1.5,
              borderTop: "1px solid #edf2f7",
              bgcolor: "#f8fafc",
              flexShrink: 0,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => setRefineDrawerOpen(false)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 1.5,
                py: 1,
                background: "linear-gradient(90deg, #0f2a7a 0%, #111b4f 100%)",
              }}
            >
              View results
            </Button>
          </Box>
        </Drawer>
      ) : null}

      <Snackbar
        open={cartNotice.open}
        autoHideDuration={4000}
        onClose={() => setCartNotice((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCartNotice((n) => ({ ...n, open: false }))}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            maxWidth: 480,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.15)",
          }}
        >
          {cartNotice.message}
        </Alert>
      </Snackbar>
    </>,
  );
}
